import { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { getInvoiceSettings, updateInvoiceSettings } from "api";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import Button from "components/atoms/Button/Button";
import Input from "components/atoms/Input/Input";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Dropdown from "components/atoms/Dropdown/Dropdown";

import errorToast from "helpers/errorToast";
import { toast } from "react-toastify";
import usePermissions from "hooks/usePermissions";

function InvoiceSettings() {
  const intl = useIntl();
  const methods = useForm({ shouldFocusError: true });
  const { setValue, getValues, handleSubmit, watch } = methods;
  watch();

  const { permissions } = usePermissions();
  const {
    permissions: {
      admin_settings: { edit_company_settings },
    },
  } = JSON.parse(permissions || "{}");

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const auditTypes = [
    {
      value: "no_audit",
      label: intl.formatMessage({
        id: "settings.company-details.auditType.noAudit",
        defaultMessage: "No audit",
      }),
    },
    {
      value: "no_invoicing",
      label: intl.formatMessage({
        id: "settings.company-details.auditType.noInvoicing",
        defaultMessage: "No invoicing",
      }),
    },
    {
      value: "self_service",
      label: intl.formatMessage({
        id: "settings.company-details.auditType.selfService",
        defaultMessage: "Self service",
      }),
    },
    {
      value: "self_service_auto",
      label: intl.formatMessage({
        id: "settings.company-details.auditType.selfServiceAuto",
        defaultMessage: "Self service (auto)",
      }),
    },
    {
      value: "cargobase",
      label: "Cargobase",
    },
  ];

  const convertArrayToCommaSeparatedString = (array: string[]) => {
    if (array.length === 1) return array[0].toString();
    let str = "";
    array.forEach((item) => {
      if (item !== array[array.length - 1]) str += `${item}, `;
      else str += item;
    });
    return str;
  };

  const convertCommaSeparatedStringToArray = (str: string) => {
    if (!str) return;
    if (str.includes(",")) {
      const array = str.split(",");
      return array.map((item) => item.trim());
    }
    return [str];
  };

  const getInvoices = () => {
    setIsLoading(true);
    getInvoiceSettings()
      .then((res) => {
        if (res.status === 200) {
          setValue("approved_invoice_emails", convertArrayToCommaSeparatedString(res.data.approved_invoice_emails));
          setValue("auto_self_service_upper_margin", res.data.auto_self_service_upper_margin);
          setValue("invoice_audit_type", res.data.invoice_audit_type);
          setValue("verify_packing_list", res.data.verify_packing_list || false);
          setIsLoading(false);
        }
      })
      .catch((err) => errorToast(err));
  };

  const onSubmit = (data: any) => {
    setIsSubmitting(true);
    if (data.invoice_audit_type !== "self_service_auto") {
      delete data.auto_self_service_upper_margin;
      delete data.verify_packing_list;
    }
    if (data.invoice_audit_type === "no_invoicing") data.approved_invoice_emails = null;
    data.approved_invoice_emails = convertCommaSeparatedStringToArray(data.approved_invoice_emails);
    updateInvoiceSettings(data)
      .then((res) => {
        if (res.status === 200) {
          toast(
            intl.formatMessage({
              id: "settings.settingsUpdated",
              defaultMessage: "Settings updated successfully",
            }),
            {
              type: "success",
            },
          );
          getInvoices();
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    getInvoices();
  }, []);

  if (isLoading) return <LoadingSkeleton noShadow rows={2} />;

  return (
    <FormProvider {...methods}>
      <form>
        <Row
          style={{
            padding: "16px 0",
          }}
        >
          <Col sm={3}>
            <div className="font-weight-bold font-b1">
              <FormattedMessage id="settings.invoice-settings" defaultMessage="Invoice settings" />
            </div>
            <span className=" font-b3">
              <FormattedMessage
                id="settings.invoice-settings.invoiceSettingsSubtext"
                defaultMessage="Please provide the invoice workflow settings"
              />
            </span>
          </Col>
          <Col sm={9} className="d-flex flex-column font-b3">
            <div className="mb-2 font-b3">
              <FormattedMessage id="settings.invoice-settings.auditFlow" defaultMessage="Invoice audit flow" />
            </div>
            <Dropdown
              useHookForm
              required
              options={auditTypes}
              name="invoice_audit_type"
              className="input-width-md mb-3"
              disabled={!edit_company_settings}
            />

            {getValues("invoice_audit_type") === "self_service_auto" && (
              <>
                <div className="mb-3">
                  <Checkbox
                    providerSections
                    name="verify_packing_list"
                    checkboxLabel={intl.formatMessage({
                      id: "settings.invoice-settings.verifyPackingList",
                      defaultMessage: "Verify packing list",
                    })}
                    disabled={!edit_company_settings}
                  />
                </div>
                <Row className="mb-3 m-0">
                  <div className="font-b3 mb-2 required">
                    <FormattedMessage
                      id="settings.invoice-settings.selfService"
                      defaultMessage="(Auto) Self service upper margin percentage"
                    />
                  </div>
                  <Input
                    className="input-width-md rounded-input"
                    required
                    type="text"
                    name="auto_self_service_upper_margin"
                    eventContext="Settings - Company Details"
                  />
                </Row>
              </>
            )}

            {getValues("invoice_audit_type") !== "no_invoicing" && (
              <div className="mb-3">
                <div className="font-b3 mb-2 required">
                  <FormattedMessage id="settings.invoice-settings.sendInvoiceTo" defaultMessage="Send approved invoice to" />
                </div>
                <div style={{ width: "100%", maxWidth: "390px" }}>
                  <Input
                    className="w-100 rounded-input"
                    name="approved_invoice_emails"
                    required
                    type="text"
                    as="textarea"
                    eventContext="Settings - Company Details"
                    placeholder={intl.formatMessage({
                      id: "settings.payment-locations.SendApprovedInvoiceToPlaceholder",
                      defaultMessage: "Comma-separated e-mails",
                    })}
                    disabled={!edit_company_settings}
                  />
                </div>
              </div>
            )}
            <Button
              style={{
                width: "106px",
                whiteSpace: "nowrap",
              }}
              className="btn-secondary"
              onClick={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
              disabled={!edit_company_settings}
            >
              <FormattedMessage id="general.saveChanges" defaultMessage="Save changes" />
            </Button>
          </Col>
        </Row>
      </form>
    </FormProvider>
  );
}

export default InvoiceSettings;
